import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, FileSignature } from "lucide-react";

export default function SignPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [sig, setSig] = useState(null);
  const [fullName, setFullName] = useState("");
  const [nif, setNif] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/signatures/${id}?token=${token}`).then(r => r.ok ? r.json() : null).then(setSig);
  }, [id, token]);

  const submit = async () => {
    if (!canvasRef.current || canvasRef.current.isEmpty()) { toast.error("Please sign first"); return; }
    if (!fullName || !nif) { toast.error("Full name and NIF required"); return; }
    setLoading(true);
    const dataUrl = canvasRef.current.getTrimmedCanvas().toDataURL("image/png");
    const base = process.env.REACT_APP_BACKEND_URL || "";
    try {
      const r = await fetch(`${base}/api/signatures/${id}/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, signer_full_name: fullName, signer_nif: nif, signature_image_png_base64: dataUrl }),
      });
      if (!r.ok) { const err = await r.json(); throw err; }
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.detail?.error || "Signature failed");
    } finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    // Anyone with token+sig info can re-fetch via signed-pdf if they have auth; for anon flow
    // we just confirm with the recorded record
    window.location.href = `${base}/api/signatures/${id}/signed-pdf`;
  };

  if (!sig) return (
    <div className="min-h-screen grid place-items-center p-6 bg-slate-100">
      <Card className="p-6 max-w-md">Loading signature request…</Card>
    </div>
  );

  if (submitted || sig.status === "signed") {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-slate-100" data-testid="sign-success">
        <Card className="p-8 max-w-md text-center">
          <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-600"/>
          <h1 className="text-2xl font-semibold mt-3 text-[#0A1F44]">Signature recorded</h1>
          <p className="text-sm text-slate-500 mt-2">Thank you. You may close this window.</p>
          <Button className="mt-4 bg-[#0A1F44]" onClick={downloadPdf}>Download signed copy</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 bg-slate-100" data-testid="sign-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <img src="/brand/logo-color.png" alt="The English Academy" className="h-10"/>
          <h1 className="text-xl font-semibold text-[#0A1F44]"><FileSignature className="inline h-5 w-5 mr-1"/>Signature required</h1>
        </div>
        <Card className="p-4 mb-4">
          <div className="text-sm"><strong>Document type:</strong> {sig.document_kind}</div>
          <div className="text-sm"><strong>Reference:</strong> <span className="font-mono text-xs">{sig.document_subject_id}</span></div>
          <div className="text-sm text-slate-500 mt-2">By signing below you acknowledge and accept the terms of this document.</div>
        </Card>
        <Card className="p-4 space-y-3">
          <div>
            <label className="text-xs uppercase font-semibold text-slate-500">Full name</label>
            <Input value={fullName} onChange={(e)=>setFullName(e.target.value)} placeholder="Nombre completo" data-testid="sign-full-name"/>
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-slate-500">NIF / NIE</label>
            <Input value={nif} onChange={(e)=>setNif(e.target.value)} placeholder="00000000X" data-testid="sign-nif"/>
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-slate-500">Signature</label>
            <div className="border-2 border-dashed border-slate-300 rounded bg-white" data-testid="sign-canvas-wrap">
              <SignatureCanvas ref={canvasRef} penColor="#0A1F44" canvasProps={{ className: "w-full h-48", style: { touchAction: "none" } }} />
            </div>
            <Button variant="ghost" size="sm" onClick={() => canvasRef.current?.clear()} data-testid="sign-clear">Clear</Button>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" data-testid="sign-decline" onClick={async () => {
              const base = process.env.REACT_APP_BACKEND_URL || "";
              await fetch(`${base}/api/signatures/${id}/decline`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, reason: "User declined" }),
              });
              toast.info("Signature declined.");
            }}>Decline</Button>
            <Button className="bg-[#C8102E] hover:bg-[#a40c25]" onClick={submit} disabled={loading} data-testid="sign-submit">{loading ? "…" : "Sign and submit"}</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
